/* eslint-disable no-nested-ternary */
import {
  CategorySearchView,
  NestedCategoryView,
  MatchingTrialStatus,
  OverallTrialStatus,
} from '@data/sdk/match';
import { FilterOption } from '../types';

export const getStaticOptions = (
  type: typeof MatchingTrialStatus | typeof OverallTrialStatus,
  key: string,
): FilterOption[] =>
  Object.values(type).map((value) => ({
    label: `trials_library.filters_options.${key}.${value}`,
    value,
  }));

export const getDynamicOptions = (fetchedCategories: CategorySearchView[]): FilterOption[] =>
  fetchedCategories.map((category) => ({
    label: category.name,
    value: category.id,
  }));

export const getDynamicNestedOptions = (nestedIcdCodes: NestedCategoryView[]): FilterOption[] =>
  nestedIcdCodes.map((nestedIcdCode) => {
    const isLeaf = nestedIcdCode.subCategories.length === 0;

    return {
      label: `${nestedIcdCode.shortName} ${nestedIcdCode.name}`,
      value: nestedIcdCode.id,
      isLeaf,
      children:
        nestedIcdCode.subCategories.length > 0
          ? getDynamicNestedOptions(nestedIcdCode.subCategories)
          : [],
    };
  });

export const handleNestedOptionsSearch = (
  nestedIcdCodesOptions: FilterOption[],
  value: string,
): FilterOption[] => {
  const filterOptionsRecursively = (options: FilterOption[]): FilterOption[] => {
    return options.reduce<FilterOption[]>((filtered, option) => {
      const matchesSearch = option.label.toLowerCase().includes(value.toLowerCase());
      const children = option.children || [];
      const filteredChildren = filterOptionsRecursively(children);

      if (matchesSearch) {
        // If parent matches, include all children
        return [
          ...filtered,
          {
            ...option,
            children,
          },
        ];
      }
      if (filteredChildren.length > 0) {
        // If children match but parent doesn't, include filtered children
        return [
          ...filtered,
          {
            ...option,
            children: filteredChildren,
          },
        ];
      }
      return filtered;
    }, []);
  };

  return filterOptionsRecursively(nestedIcdCodesOptions);
};

export const getBottomLevelOptions = (paramIds: string[], options: FilterOption[]): string[] => {
  const result: string[] = [];

  const findBottomLevelIds = (option: FilterOption, isParentSelected: boolean) => {
    const isCurrentSelected = isParentSelected || paramIds.includes(option.value);

    if (!option.children?.length) {
      // Only add leaf node if it or any of its ancestors were selected
      if (isCurrentSelected) {
        result.push(option.value);
      }
      return;
    }

    // Extract shortName from label (format: "A00 Name" or "A00.9 Name")
    const hasLevel3Children = option.children.some((child) => {
      const shortName = child.label.split(' ')[0]; // Get the code part (e.g., "A00.9")
      return shortName.includes('.');
    });

    if (hasLevel3Children && isCurrentSelected) {
      result.push(option.value);
      option.children.forEach((child) => {
        result.push(child.value);
      });
      return;
    }

    option.children.forEach((child) => findBottomLevelIds(child, isCurrentSelected));
  };

  options.forEach((option) => findBottomLevelIds(option, false));

  return result;
};
